/**
 * DomainConfirmation - Component to display a domain for confirmation
 * Shows the domain in a bordered box similar to other field confirmations
 */

import React, { FC } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { FiGlobe } from 'react-icons/fi';
import { useRealtimeChatContext } from '../hooks/useRealtimeChatContext';

interface DomainConfirmationProps {
  domain: string;
}

const DomainConfirmation: FC<DomainConfirmationProps> = ({ domain }) => {
  const { sendMessage } = useRealtimeChatContext();

  const handleConfirm = () => {
    sendMessage(`I want to use the domain ${domain}`);
  };

  return (
    <Box
      border="2px solid"
      borderColor="green.400"
      borderRadius="xl"
      bg="green.50"
      p={5}
      maxW="550px"
      cursor="pointer"
      onClick={handleConfirm}
      _hover={{
        bg: 'green.100',
        borderColor: 'green.500',
      }}
      transition="all 0.2s ease-in-out"
    >
      <VStack spacing={3} align="stretch">
        <HStack justify="space-between" align="center">
          <HStack spacing={2}>
            <Icon as={FiGlobe} color="green.600" boxSize={5} />
            <Text
              fontSize="sm"
              fontWeight="semibold"
              color="green.700"
              textTransform="uppercase"
              letterSpacing="wide"
              fontFamily="'Noto Sans', 'Open Sans', sans-serif"
            >
              Your Website Domain
            </Text>
          </HStack>
        </HStack>
        
        <Box
          bg="white"
          borderRadius="lg"
          p={4}
          border="1px solid"
          borderColor="green.200"
        >
          <Text
            fontSize="lg"
            fontWeight="bold"
            color="gray.800"
            fontFamily="'Noto Sans', 'Open Sans', sans-serif"
            wordBreak="break-all"
          >
            {domain}
          </Text>
        </Box>

        <Text
          fontSize="xs"
          color="gray.500"
          textAlign="center"
          fontFamily="'Noto Sans', 'Open Sans', sans-serif"
        >
          Click to confirm this domain
        </Text>
      </VStack>
    </Box>
  );
};

export default DomainConfirmation;
